import type { DashboardStats } from '@/shared/api/types/dashboard-types'
import { PieChart as PieChartIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { Skeleton } from '@/shared/ui/skeleton'

// Donut chart display constants
const DONUT_INNER_RADIUS = 55
const DONUT_OUTER_RADIUS = 80
const DONUT_PADDING_ANGLE = 2
const CHART_HEIGHT = 220
const CHART_TOOLTIP_STYLE = {
  border: '1px solid #E4E4E7',
  borderRadius: 8,
  fontSize: 12,
  boxShadow: 'none',
}

const STATUS_COLORS: Record<string, string> = {
  active: '#2563EB',
  inactive: '#A1A1AA',
}

interface DashboardStatusChartProps {
  distribution: DashboardStats['poiStatusDistribution']
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-zinc-400 gap-2">
      <PieChartIcon className="h-8 w-8 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

export function DashboardStatusChart({ distribution }: DashboardStatusChartProps) {
  const { t } = useTranslation()

  const entries = [
    { name: t('common.active'), value: distribution.active, key: 'active' },
    { name: t('common.inactive'), value: distribution.inactive, key: 'inactive' },
  ]

  const total = entries.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5">
      <h3 className="text-sm font-semibold text-zinc-900 mb-4">
        {t('dashboard.statusDistribution')}
      </h3>

      {total === 0 ? (
        <EmptyState message={t('common.noData')} />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <Pie
              data={entries}
              dataKey="value"
              nameKey="name"
              innerRadius={DONUT_INNER_RADIUS}
              outerRadius={DONUT_OUTER_RADIUS}
              paddingAngle={DONUT_PADDING_ANGLE}
            >
              {entries.map((entry) => (
                <Cell key={entry.key} fill={STATUS_COLORS[entry.key] ?? '#D4D4D8'} />
              ))}
            </Pie>
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Legend iconType="circle" iconSize={8} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export function DashboardStatusChartSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5">
      <Skeleton className="h-4 w-36 mb-4" />
      <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
        <Skeleton className="h-40 w-40 rounded-full" />
      </div>
    </div>
  )
}
